import { EA_CANVAS_BASE, pathWithinEaCanvas } from "@/config/guidedFlow";

/** EA Strategic Canvas — paths inside `/ea-canvas`. */
export const ROUTE_LABELS: Record<string, string> = {
  "/": "Executive Dashboard",
  "/north-stars": "Vision & North Stars",
  "/scales": "Scales & Definitions",
  "/pillars": "Business Pillars",
  "/phases": "Phase Tracker",
  "/anatomy": "Enterprise Anatomy",
  "/heatmap": "Capability Heatmap",
  "/app-matrix": "App Contribution",
  "/architecture": "Architecture",
  "/enterprise-blueprint": "Architecture",
  "/findings": "Key Findings",
  "/roadmap": "Roadmap",
  "/governance": "Governance",
  "/reports": "Reports",
  "/configuration": "Configuration",
  "/data-management": "Configuration",
};

const EXACT_APP_LABELS: Record<string, string> = {
  "/": "Dashboard",
  "/dashboard/risk-controls-matrix": "Risks & controls matrix",
  "/analytics/receivables": "Collections Center",
  "/analytics/collections-dashboard": "Collections Center",
  "/analytics/collections-ops": "Collections Center",
  "/simulation/c2c-monte-carlo": "Collections Center",
  "/reporting": "Reporting",
  "/info": "Information",
  "/announcements": "Announcements",
  "/compliances/dashboard": "Compliance dashboard",
  "/compliances": "Compliance",
  "/standards": "Standards & references",
  "/compliance-status": "Compliance status",
  "/ticket": "Tickets",
  "/tickets": "Tickets",
  "/ticketsSupCompliance": "Tickets",
  "/organization-chart": "Organization chart",
  "/oneboxMessage": "Messages",
  "/catalogs": "Catalogs",
  "/catalogs/dashboard": "Catalogs dashboard",
  "/catalogs/collection-sops": "Collection SOPs",
  "/catalogs/sop-masters": "SOP masters",
  "/data-inventory/dashboard": "Data inventory",
  "/QuestionControlTree": "Question control tree",
  "/control": "Control catalog",
  "/controls": "Control catalog",
  "/controls/create": "Create control",
  "/user": "User management",
  "/user/permissions": "User permissions",
  "/permission/groups-pages": "Groups & pages",
  "/permission": "Permissions",
  "/notification": "Notifications",
  "/team-onboarding": "Team onboarding",
  "/objective": "Objectives",
  "/surveys": "Surveys",
  "/bankQuestions": "Bank questions",
  "/frameWork": "Control landscape",
  "/assessment-score": "Assessment score",
  "/viewTaskCalender": "Task calendar",
  "/rcsa-compliance": "RCSA",
  "/impactarea": "Impact areas",
  "/create-survey": "Create survey",
  "/createQuestion": "Create question",
  "/certifications": "Certifications",
  "/assessment": "Assessment",
  "/documents": "Documents",
  "/documents/upload": "Upload document",
  "/documents/dashboard": "Documents dashboard",
  "/documents/notifications": "Document notifications",
  "/documents/structure": "Document structure",
  "/dataresource": "Data sources",
  "/guide": "User guide",
  "/demo-partners": "Demo partners",
  "/demo-partner/questionnaires": "Partner questionnaires",
  "/demo-partner/documents": "Partner documents",
  "/demo-partner/status": "Partner status",
  "/demo-partner/tickets": "Partner tickets",
  "/demo-partner/certifications": "Partner certifications",
  "/demo-partner/assessment-requests": "Assessment requests",
  "/demo-partner/profile": "Partner profile",
  "/demo-partner/visibility": "Partner visibility",
  "/function": "Functions",
  "/services": "Services",
  "/document-categories": "Document categories",
  "/configuration/dashboard": "Configuration dashboard",
  "/configuration/services-status": "Services status",
  "/configuration/lifecycle-stages": "Lifecycle stages",
  "/configuration/criticalities": "Criticalities",
  "/configuration/asset-types": "Asset types",
  "/configuration/asset-classifications": "Asset classifications",
  "/catalogs/services": "Services catalog",
  "/catalogs/services/create": "Create service",
  "/catalogs/resources": "Resources",
  "/catalogs/resources/create": "Create resource",
  "/ea/dashboard": "EA dashboard",
  "/ea/principles": "EA principles",
  "/ea/applications": "EA applications",
  "/ea/work-packages": "EA work packages",
  "/ea/scope-items": "EA scope items",
  "/ea/relations-tree": "EA relations tree",
  "/process-builder": "Process builder",
  "/modeler": "Process modeler",
  "/instances": "Process instances",
  "/tasks": "Task inbox",
  "/monitor": "Live monitor",
  "/agents": "Agents",
  "/metrics": "Metrics Dashboard",
  "/metrics/definitions": "Metric Definitions",
  "/metrics/process": "Process Analytics",
  "/process/viewer": "Process viewer",
  "/policy": "Policy library",
  "/policy/categories": "Policy categories",
  "/policy/obligations": "Regulatory obligations",
  "/policy/approvals": "Policy approvals",
  "/policy/attestation": "Attestation",
  "/policy/versions": "Policy versions",
  "/policy/audit": "Policy audit log",
  "/audit/activity-log": "Activity log",
  "/policy/settings": "Policy settings",
  "/subBusinessUnit": "Sub-business unit",
  "/jobTitle": "Job title",
  "/createCurrency": "Currency",
  "/discoveredTypes": "Discovered types",
  "/rootCause": "Root cause",
  "/system": "System",
  "/subFunction": "Sub-function",
  "/activities": "Activities",
  "/riskCategory": "Risk category",
  "/controlReference": "Control reference",
  "/accountType": "Account type",
  "/eventType": "Event type",
  "/financialImpacts": "Financial impacts",
  "/process": "Process",
  "/createImpact": "Create impact",
  "/createsurvey": "Create survey",
  "/viewsurvey": "View survey",
  "/create": "Create",
};

const PREFIX_APP_LABELS_RAW: [string, string][] = [
  ["/compliances/view-control", "View control"],
  ["/compliances/edit", "Edit compliance"],
  ["/compliances/status", "Compliance status"],
  ["/compliances/dashboard", "Compliance dashboard"],
  ["/catalogs/services/create", "Service catalog"],
  ["/catalogs/resources/edit", "Edit resource"],
  ["/catalogs/resources/create", "Create resource"],
  ["/controls/update", "Update control"],
  ["/updatecontrol", "Update control"],
  ["/documents", "Document details"],
  ["/documents/upload", "Upload document"],
  ["/documents/dashboard", "Documents dashboard"],
  ["/documents/notifications", "Notifications"],
  ["/documents/structure", "Document structure"],
  ["/dataresource/update", "Update data source"],
  ["/dataresource", "Data source"],
  ["/exportsurvey", "Export survey"],
  ["/export-survey", "Export survey"],
  ["/assessmentsurvey", "Assessment survey"],
  ["/assessment-survey", "Assessment survey"],
  ["/updatesurvey", "Update survey"],
  ["/updateSurvey", "Update survey"],
  ["/updateQuestion", "Update question"],
  ["/metrics/process", "Process Analytics"],
  ["/instances", "Instance"],
  ["/process", "Process detail"],
  ["/controls", "Control"],
  ["/updateImpacted", "Update impact"],
  ["/compliances", "Compliance"],
  ["/catalogs/resources", "Resources"],
  ["/catalogs/services", "Services catalog"],
  ["/catalogs", "Catalogs"],
  ["/configuration", "Configuration"],
  ["/demo-partner", "Partner"],
  ["/policy", "Policy"],
  ["/ea/", "Enterprise architecture"],
  ["/bankQuestions", "Bank questions"],
];

const PREFIX_APP_LABELS: [string, string][] = [...PREFIX_APP_LABELS_RAW].sort(
  (a, b) => b[0].length - a[0].length
);

function humanizePathTail(path: string): string {
  const segment = path.split("/").filter(Boolean).pop() ?? "Page";
  if (/^\d+$/.test(segment)) return "Details";
  return segment
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function breadcrumbPageLabel(pathname: string): string {
  const raw = pathname.replace(/\/$/, "") || "/";

  if (raw.startsWith(EA_CANVAS_BASE)) {
    const inner = pathWithinEaCanvas(pathname).replace(/\/$/, "") || "/";
    if (ROUTE_LABELS[inner]) return ROUTE_LABELS[inner];
    if (inner === "/" || inner === "") return ROUTE_LABELS["/"];
    return humanizePathTail(inner);
  }

  if (EXACT_APP_LABELS[raw]) return EXACT_APP_LABELS[raw];

  for (const [prefix, label] of PREFIX_APP_LABELS) {
    if (raw === prefix || raw.startsWith(`${prefix}/`)) {
      return label;
    }
  }

  return humanizePathTail(raw);
}

export function isBreadcrumbLandingPage(pathname: string): boolean {
  const raw = pathname.replace(/\/$/, "") || "/";
  if (raw === "/") return true;
  const base = EA_CANVAS_BASE.replace(/\/$/, "");
  return raw === base;
}

/** i18n key suffix under `pageDescriptions.*` (see locales). */
const EA_INNER_PAGE_DESC_KEYS: Record<string, string> = {
  "/": "eaExecutiveDashboard",
  "/north-stars": "eaNorthStars",
  "/scales": "eaScales",
  "/pillars": "eaPillars",
  "/anatomy": "eaAnatomy",
  "/heatmap": "eaHeatmap",
  "/app-matrix": "eaAppMatrix",
  "/enterprise-blueprint": "eaArchitecture",
  "/architecture": "eaArchitecture",
  "/findings": "eaFindings",
  "/roadmap": "eaRoadmap",
  "/governance": "eaGovernance",
  "/reports": "eaReports",
  "/data-management": "eaDataManagement",
};

/** Exact app paths → description key (omit to fall through to prefix / default). */
const EXACT_APP_PAGE_DESC_KEYS: Record<string, string> = {
  "/": "dashboardHome",
  "/controls": "controlCatalog",
  "/controls/create": "createControl",
  "/compliances/dashboard": "complianceModuleDashboard",
  "/standards": "standardsReferences",
  "/objective": "objectivesPage",
  "/audit/activity-log": "activityLogPage",
};

const PREFIX_APP_PAGE_DESC_KEYS_RAW: [string, string][] = [
  ["/compliances/view-control", "viewControl"],
  ["/compliances/edit", "editCompliance"],
  ["/compliances/status", "complianceStatus"],
  ["/compliances/dashboard", "complianceModuleDashboard"],
  ["/catalogs/services/create", "createService"],
  ["/catalogs/resources/edit", "editResource"],
  ["/catalogs/resources/create", "createResource"],
  ["/controls/update", "updateControl"],
  ["/updatecontrol", "updateControl"],
  ["/documents", "documents"],
  ["/documents/upload", "uploadDocument"],
  ["/documents/dashboard", "documentsDashboard"],
  ["/documents/notifications", "documentNotifications"],
  ["/documents/structure", "documentStructure"],
  ["/dataresource/update", "updateDataSource"],
  ["/dataresource", "dataSources"],
  ["/exportsurvey", "exportSurvey"],
  ["/export-survey", "exportSurvey"],
  ["/assessmentsurvey", "assessmentSurvey"],
  ["/assessment-survey", "assessmentSurvey"],
  ["/updatesurvey", "updateSurvey"],
  ["/updateSurvey", "updateSurvey"],
  ["/updateQuestion", "updateQuestion"],
  ["/instances", "processInstances"],
  ["/process", "processDetail"],
  ["/controls", "controlCatalog"],
  ["/updateImpacted", "updateImpact"],
  ["/compliances", "compliance"],
  ["/catalogs/resources", "resources"],
  ["/catalogs/services", "servicesCatalog"],
  ["/catalogs", "catalogs"],
  ["/configuration", "configuration"],
  ["/demo-partner", "demoPartner"],
  ["/policy", "policyLibrary"],
  ["/ea/", "eaGeneric"],
  ["/bankQuestions", "bankQuestions"],
];

const PREFIX_APP_PAGE_DESC_KEYS: [string, string][] = [...PREFIX_APP_PAGE_DESC_KEYS_RAW].sort(
  (a, b) => b[0].length - a[0].length
);

/**
 * i18n key suffix for static page description (`pageDescriptions.<key>`).
 * Dynamic pages should use `usePageHeadingOverride` instead.
 */
export function pageDescriptionI18nKey(pathname: string): string {
  const raw = pathname.replace(/\/$/, "") || "/";

  if (raw.startsWith(EA_CANVAS_BASE)) {
    const inner = pathWithinEaCanvas(pathname).replace(/\/$/, "") || "/";
    return EA_INNER_PAGE_DESC_KEYS[inner] ?? "default";
  }

  if (EXACT_APP_PAGE_DESC_KEYS[raw]) return EXACT_APP_PAGE_DESC_KEYS[raw];

  for (const [prefix, key] of PREFIX_APP_PAGE_DESC_KEYS) {
    if (raw === prefix || raw.startsWith(`${prefix}/`)) {
      return key;
    }
  }

  return "default";
}

/** @deprecated Use breadcrumbPageLabel */
export function labelForPathname(pathname: string): string {
  return breadcrumbPageLabel(pathname);
}
